import { useState, useCallback, useEffect } from 'react';
import { db } from '../config/firebase';
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { logError, logInfo } from '../utils/logger';
import { syncPendingData } from '../utils/offlineStorage';

export const useConnectionStatus = () => {
  const [status, setStatus] = useState('online');
  const [pendingUploads, setPendingUploads] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [lastSyncAttempt, setLastSyncAttempt] = useState(null);

  const forceSyncData = useCallback(async () => {
    if (!navigator.onLine) {
      logError('Cannot sync while offline');
      return false;
    }

    try {
      await syncPendingData();
      setLastSyncAttempt(new Date());
      logInfo('Force sync completed');
      return true;
    } catch (error) {
      logError('Force sync failed:', error);
      return false;
    }
  }, []);

  const clearPendingData = useCallback(async () => {
    try {
      const pendingCollection = collection(db, 'pendingUploads');
      const snapshot = await getDocs(pendingCollection);
      
      if (snapshot.empty) {
        logInfo('No pending data to clear');
        return true;
      }

      const deletePromises = snapshot.docs.map(async (doc) => {
        try {
          await deleteDoc(doc.ref);
          logInfo(`Cleared pending item: ${doc.id}`);
        } catch (err) {
          logError(`Failed to delete item ${doc.id}:`, err);
        }
      });

      await Promise.all(deletePromises);
      logInfo('All pending data cleared successfully');
      return true;
    } catch (error) {
      logError('Failed to clear pending data:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setStatus('online');
      logInfo('Connection restored');
      forceSyncData();
    };

    const handleOffline = () => {
      setStatus('offline');
      logInfo('Connection lost');
    };

    // Set initial status
    setStatus(navigator.onLine ? 'online' : 'offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Monitor pending uploads
    const pendingCollection = collection(db, 'pendingUploads');
    const pendingQuery = query(
      pendingCollection,
      orderBy('timestamp', 'desc')
    );
    
    const unsubscribe = onSnapshot(pendingQuery,
      (snapshot) => {
        const items = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp || new Date().toISOString()
        }));
        
        setPendingItems(items);
        setPendingUploads(items.length);
        
        // Only show pending status if there are actually pending items
        if (navigator.onLine) {
          setStatus(items.length > 0 ? 'pending' : 'online');
        }
      },
      (error) => {
        if (error.code === 'permission-denied') {
          logInfo('Pending uploads monitoring not available');
        } else {
          logError('Error monitoring pending uploads:', error);
        }
      }
    );

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, [forceSyncData]);

  return { 
    status, 
    pendingUploads,
    pendingItems,
    forceSyncData,
    clearPendingData
  };
};